import { useState } from "react";
import { Layers, ArrowRight, ArrowLeft, MessageCircleHeart } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { celebrate } from "@/lib/celebrate";
import { useAuth } from "@/context/AuthContext";
import { StageBuilder, StageLadder, StageProposalCard } from "@/components/client/StageBuilder";

/** Per-habit stage controls: ladder, move forward / step back, edit stages, ask the clinician. */
export function StagePanel({ habit: h, onChange }) {
  const { user } = useAuth();
  const [editing, setEditing] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const stage = h.stage;
  const save = async (stages, index) => {
    setBusy(true);
    try { await api.put(`/habits/${h.id}/stages`, { stages, stage_index: index }); toast.success("Stages saved. Only the current one is measured each day."); setEditing(false); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save the stages"); }
    finally { setBusy(false); }
  };
  const move = async (direction) => {
    setBusy(true);
    try {
      const r = await api.post(`/habits/${h.id}/stages/move`, { direction });
      if (direction === "forward") celebrate(false);
      toast.success(r.data.message, { duration: 6000 }); setConfirm(null); onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't move just now"); }
    finally { setBusy(false); }
  };
  const ask = async () => {
    try { const r = await api.post(`/habits/${h.id}/stages/request-draft`); toast.success(r.data.message); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send that"); }
  };
  return (
    <div className="mt-3" data-testid={`stage-panel-${h.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-stone-500"><Layers className="w-3 h-3" /> {stage ? `In stages · ${stage.index + 1} of ${stage.count}` : "Single measure"}</span>
        {!editing && <button data-testid={`stages-edit-${h.id}`} onClick={() => setEditing(true)} className="text-[11px] font-semibold text-amber-800 hover:text-amber-900">{stage ? "Edit stages" : "Build it in stages"}</button>}
        {!editing && user?.clinician_id && <button data-testid={`stages-ask-clinician-${h.id}`} onClick={ask} className="text-[11px] font-semibold text-emerald-800 hover:text-emerald-900 inline-flex items-center gap-1"><MessageCircleHeart className="w-3 h-3" /> Ask my coach to draft this</button>}
      </div>
      {stage && !editing && (
        <div className="mt-2 space-y-2">
          <StageLadder stage={stage} testid={`stage-ladder-${h.id}`} />
          {confirm ? (
            <div className="rounded-xl border border-stone-200 bg-stone-50 p-3 text-xs text-stone-700" data-testid={`stage-move-confirm-${h.id}`}>
              {confirm === "forward" ? <>Finish <strong>{stage.current}</strong> and move to <strong>{stage.names[stage.index + 1]}</strong>? Its tiers take over from tomorrow.</> : <>Adjust the plan and go back to <strong>{stage.names[stage.index - 1]}</strong> from tomorrow? Nothing you've earned changes.</>}
              <div className="flex gap-2 mt-2">
                <button onClick={() => move(confirm)} disabled={busy} data-testid={`stage-move-${confirm}-confirm-${h.id}`} className="amber-btn text-xs py-1.5">{busy ? "…" : confirm === "forward" ? "Yes, move on" : "Yes, step back"}</button>
                <button onClick={() => setConfirm(null)} className="text-xs font-semibold px-3 py-1.5 rounded-full border border-stone-300 bg-white">Cancel</button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2 flex-wrap">
              {!stage.is_last && <button onClick={() => setConfirm("forward")} data-testid={`stage-move-forward-${h.id}`} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-stone-900 text-white hover:bg-emerald-700 inline-flex items-center gap-1">Move to the next stage <ArrowRight className="w-3.5 h-3.5" /></button>}
              {stage.index > 0 && <button onClick={() => setConfirm("back")} data-testid={`stage-move-back-${h.id}`} className="text-xs font-semibold px-3 py-1.5 rounded-full border border-stone-300 bg-white text-stone-700 inline-flex items-center gap-1"><ArrowLeft className="w-3.5 h-3.5" /> Step back a stage</button>}
            </div>
          )}
        </div>
      )}
      {editing && <div className="mt-3"><StageBuilder initial={stage?.stages} index={stage?.index || 0} kind={h.kind} onSave={save} onCancel={() => setEditing(false)} busy={busy} title={h.title} category={h.category} /></div>}
      <StageProposalCard habit={h} onAnswered={onChange} />
    </div>
  );
}
